import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import { Box, Button, Grid2, Paper, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Link } from "@tanstack/react-router";
import { QueryStatusRouter, Select } from "bi-lib";

interface Props {
    id: number;
}

export default function AssignmentBillingForm({ id }: Props) {
    const { completeAssignment, updateBilling } = useAssignmentData({ id });
    const { billingTermsList, invoiceTypes, invoiceStylesList } = useLookupListData();

    const form = useForm({
        defaultValues: {
            billingContactEmail: completeAssignment.data?.contactEmail,
            billingContactPhone: completeAssignment.data?.contactPhone,
            billingContactName: completeAssignment.data?.assignContact,
            billingAddressTitle: completeAssignment.data?.billingNameTitle,
            billingAddressLine1: completeAssignment.data?.billingAddress1,
            billingAddressLine2: completeAssignment.data?.billingAddress2,
            billingAddressLine3: completeAssignment.data?.billingAddress3,
            billingComment: completeAssignment.data?.billingComment,
            billingTermsId: completeAssignment.data?.billingTermId,
            invoiceTypesId: completeAssignment.data?.invoiceTypeId,
            draftStylesId: completeAssignment.data?.invoiceStyleDraftId,
            finalStylesId: completeAssignment.data?.invoiceStyleFinalId,
        },
        onSubmit: ({ value }) => {
            updateBilling.mutate({
                assignmentId: id,
                billingContactEmail: value.billingContactEmail,
                billingContactPhone: value.billingContactPhone,
                billingContactName: value.billingContactName,
                billingAddressTitle: value.billingAddressTitle,
                billingAddressLine1: value.billingAddressLine1,
                billingAddressLine2: value.billingAddressLine2,
                billingAddressLine3: value.billingAddressLine3,
                billingComment: value.billingComment,
                billingTermsId: value.billingTermsId?.toString(),
                invoiceTypesId: value.invoiceTypesId?.toString(),
                draftStylesId: value.draftStylesId?.toString(),
                finalStylesId: value.finalStylesId?.toString(),
            });
        },
    });

    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <Stack spacing={2}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={6}>
                            <Stack spacing={2}>
                                <Paper sx={{ padding: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography variant={"h5"}>Contact Information</Typography>
                                        <form.Field name={"billingContactName"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Name"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"billingContactEmail"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Email"}
                                                    type={"email"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"billingContactPhone"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Phone"}
                                                    type={"tel"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                    </Stack>
                                </Paper>
                                <Paper sx={{ padding: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography variant={"h5"}>Billing Address</Typography>
                                        <form.Field name={"billingAddressTitle"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Title"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"billingAddressLine1"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Address Line 1"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"billingAddressLine2"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Address Line 2"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"billingAddressLine3"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Address Line 3"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid2>
                        <Grid2 size={6}>
                            <Paper sx={{ padding: 2 }}>
                                <Stack spacing={1}>
                                    <Typography variant={"h5"}>Billing Information</Typography>
                                    <QueryStatusRouter queryHook={billingTermsList}>
                                        <form.Field name={"billingTermsId"}>
                                            {(field) => (
                                                <Select
                                                    label={"Billing Term"}
                                                    value={field.state.value}
                                                    onChange={(e) => field.handleChange(e)}
                                                    options={billingTermsList.data?.map((x) => ({
                                                        label: x.shortDescription,
                                                        value: x.billingTermId,
                                                    }))}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"invoiceTypesId"}>
                                            {(field) => (
                                                <Select
                                                    label={"Invoice Type"}
                                                    value={field.state.value}
                                                    onChange={(e) => field.handleChange(e)}
                                                    options={invoiceTypes.data?.map((x) => ({
                                                        label: x.invoiceType,
                                                        value: x.invoiceTypeId,
                                                    }))}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"draftStylesId"}>
                                            {(field) => (
                                                <Select
                                                    label={"Draft Invoice Print Style"}
                                                    value={field.state.value}
                                                    onChange={(e) => field.handleChange(e)}
                                                    options={invoiceStylesList.data?.map((x) => ({
                                                        label: x.description,
                                                        value: x.invoiceStyleId,
                                                    }))}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"finalStylesId"}>
                                            {(field) => (
                                                <Select
                                                    label={"Final Invoice Print Style"}
                                                    value={field.state.value}
                                                    onChange={(e) => field.handleChange(e)}
                                                    options={invoiceStylesList.data?.map((x) => ({
                                                        label: x.description,
                                                        value: x.invoiceStyleId,
                                                    }))}
                                                />
                                            )}
                                        </form.Field>
                                        <form.Field name={"billingComment"}>
                                            {(field) => (
                                                <TextField
                                                    label={"Bill Comment"}
                                                    value={field.state.value ?? ""}
                                                    onChange={(e) => field.handleChange(e.target.value)}
                                                />
                                            )}
                                        </form.Field>
                                    </QueryStatusRouter>
                                </Stack>
                            </Paper>
                        </Grid2>
                    </Grid2>
                    <Box display={"flex"} justifyContent={"end"} gap={1}>
                        <Link to={"/assignment"}>
                            <Button variant={"outlined"}>Cancel</Button>
                        </Link>
                        <Button type={"submit"} variant={"contained"}>
                            Save
                        </Button>
                    </Box>
                </Stack>
            </form>
        </>
    );
}
